import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { CaretRight, ChartBar, Users, CheckCircle } from "@phosphor-icons/react";
import { SALES_STATUS_COLORS } from "@/pages/Sales";

const STATUS_BAR = {
  Lead: "bg-slate-400",
  "In Progress": "bg-amber-400",
  Finalized: "bg-emerald-500",
};

export default function SalesAnalytics({ onOpenClient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // `status:Lead` | `assignee:email`

  useEffect(() => {
    api.get("/sales/analytics")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-slate-500" data-testid="sales-analytics-loading">Loading analytics…</p>;
  if (!data) return null;

  const maxStatus = Math.max(1, ...data.by_status.map((s) => s.count));
  const maxAssignee = Math.max(1, ...data.by_assignee.map((a) => a.count));

  const Drill = ({ id, clients }) => (
    <AnimatePresence>
      {open === id && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="overflow-hidden"
          data-testid={`sales-drill-${id}`}
        >
          <div className="mt-2 ml-1 pl-3 border-l-2 border-slate-200 space-y-1 py-1 max-h-72 overflow-y-auto">
            {clients.length === 0 ? (
              <p className="text-xs text-slate-400">No clients.</p>
            ) : clients.map((c) => (
              <button
                key={c.email}
                onClick={() => onOpenClient(c.email)}
                className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 hover:underline w-full text-left"
                data-testid={`sales-drill-client-${c.email}`}
              >
                <CaretRight size={10} className="text-slate-300" /> {c.name}
                <span className="text-slate-400 font-mono">{c.email}</span>
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="space-y-8" data-testid="sales-analytics">
      <p className="text-sm text-slate-500">
        <span className="font-medium text-slate-900">{data.total}</span> clients in the pipeline. Click any row to see the clients that make it up.
      </p>

      {/* By status */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center gap-2 label-tracking mb-4"><ChartBar size={14} /> By status</div>
        <div className="space-y-3">
          {data.by_status.map((s) => {
            const id = `status:${s.status}`;
            return (
              <div key={s.status}>
                <button
                  onClick={() => setOpen(open === id ? null : id)}
                  className="w-full flex items-center gap-3 group"
                  data-testid={`sales-stat-status-${s.status.replace(/\s+/g, "-")}`}
                >
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-sm w-28 text-left shrink-0 ${SALES_STATUS_COLORS[s.status]}`}>{s.status}</span>
                  <div className="flex-1 h-6 bg-slate-100 rounded-sm overflow-hidden">
                    <div className={`h-full ${STATUS_BAR[s.status]} transition-all`} style={{ width: `${(s.count / maxStatus) * 100}%` }} />
                  </div>
                  <span className="text-sm font-mono text-slate-700 w-10 text-right">{s.count}</span>
                  <CaretRight size={14} className={`text-slate-300 transition-transform ${open === id ? "rotate-90" : ""}`} />
                </button>
                <Drill id={id} clients={s.clients} />
              </div>
            );
          })}
        </div>
      </div>

      {/* By assignee */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center gap-2 label-tracking mb-4"><Users size={14} /> By assigned user</div>
        <div className="space-y-3">
          {data.by_assignee.map((a) => {
            const id = `assignee:${a.email || "unassigned"}`;
            return (
              <div key={id}>
                <button
                  onClick={() => setOpen(open === id ? null : id)}
                  className="w-full flex items-center gap-3"
                  data-testid={`sales-stat-assignee-${a.email || "unassigned"}`}
                >
                  <span className="text-xs text-slate-700 w-40 text-left shrink-0 truncate">{a.name}</span>
                  <div className="flex-1 h-6 bg-slate-100 rounded-sm overflow-hidden">
                    <div className="h-full bg-indigo-400 transition-all" style={{ width: `${(a.count / maxAssignee) * 100}%` }} />
                  </div>
                  <span className="text-sm font-mono text-slate-700 w-10 text-right">{a.count}</span>
                  <CaretRight size={14} className={`text-slate-300 transition-transform ${open === id ? "rotate-90" : ""}`} />
                </button>
                <Drill id={id} clients={a.clients} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
